import { useEffect, useRef, useState } from 'react';
import type { Mode } from '@tigrinho/engine';
import { navigate } from '../App';
import { MODE_LABELS, PAWNS, SEAT_COLORS } from '../lib/content';
import { pawnArt } from '../lib/art';
import { useRoomCtx } from './RoomScreen';
import { RulesSheet } from './Home';

/** Mesa privada no estilo do Business Tour: um cartão por jogador e o botão JOGAR embaixo. */
export function Lobby() {
  const { room, you, send } = useRoomCtx();
  const [copied, setCopied] = useState(false);
  const [rules, setRules] = useState(false);
  const me = room.seats.find((s) => s.id === you);
  const isHost = room.hostId === you;
  const link = `${location.origin}/sala/${room.code}`;
  const humansWaiting = room.seats.filter((s) => !s.bot && s.id !== room.hostId && !s.ready);
  const canStart = room.seats.length >= 2 && humansWaiting.length === 0;
  // Só o peão de outra pessoa fica bloqueado; o de um bot pode ser escolhido (o bot troca).
  const taken = new Set(room.seats.filter((s) => s.id !== you && !s.bot).map((s) => s.pawn));
  const invite = `Bora jogar Tigrinho Tour? Entra na mesa ${room.code}: ${link}`;

  // "Treinar com bots": quem criou a mesa já ganha os bots pedidos.
  const botsAsked = useRef(false);
  useEffect(() => {
    const want = Number(new URLSearchParams(location.search).get('bots') ?? 0);
    if (!isHost || botsAsked.current || !want) return;
    botsAsked.current = true;
    for (let k = room.seats.length - 1; k < Math.min(3, want); k++) send({ t: 'addBot' });
    history.replaceState(null, '', location.pathname);
  }, [isHost, room.seats.length, send]);

  async function share() {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Tigrinho Tour', text: invite, url: link });
        return;
      }
    } catch {
      /* cancelou o compartilhamento */
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* sem permissão para copiar */
    }
  }

  const nextPawn = (dir: 1 | -1) => {
    if (!me) return;
    for (let k = 1; k <= PAWNS.length; k++) {
      const p = (me.pawn + dir * k + PAWNS.length * 2) % PAWNS.length;
      if (!taken.has(p)) return send({ t: 'setPawn', pawn: p });
    }
  };

  return (
    <main className="table-screen">
      <i className="sky-squares" aria-hidden="true" />
      <header className="screen-top">
        <button
          className="round-btn round-back"
          onClick={() => {
            send({ t: 'leave' });
            navigate('/');
          }}
          aria-label="Sair da mesa"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 5 L7 12 L14 19" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="ribbon ribbon-yellow screen-title">
          <h1>Mesa privada</h1>
        </div>
        <button className="round-btn" onClick={() => setRules(true)} aria-label="Como jogar">
          <span aria-hidden="true">?</span>
        </button>
      </header>

      <section className="table-settings" aria-label="Convite e duração">
        <div className="ts-block">
          <span className="ts-label">Código</span>
          <span className="ts-code" aria-label={`Código: ${room.code.split('').join(' ')}`}>
            {room.code}
          </span>
          <button className="pill pill-blue pill-small" onClick={share}>
            {copied ? 'Copiado!' : 'Copiar link'}
          </button>
          <a className="pill pill-whatsapp pill-small" href={`https://wa.me/?text=${encodeURIComponent(invite)}`} target="_blank" rel="noreferrer">
            WhatsApp
          </a>
        </div>
        <div className="ts-block" role="radiogroup" aria-label="Duração máxima">
          <span className="ts-label">Tempo</span>
          {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
            <button
              key={m}
              role="radio"
              aria-checked={room.settings.mode === m}
              className={`seg ${room.settings.mode === m ? 'is-on' : ''}`}
              disabled={!isHost}
              onClick={() => send({ t: 'settings', mode: m })}
            >
              {MODE_LABELS[m].minutes} min
            </button>
          ))}
        </div>
      </section>

      <ul className="seat-cards">
        {[0, 1, 2, 3].map((k) => {
          const s = room.seats[k];
          if (!s) {
            return (
              <li key={`empty${k}`} className="seat-card is-empty">
                <div className="seat-head" style={{ '--seat': '#9aa7b8' } as React.CSSProperties}>
                  <span className="seat-avatar" />
                  <span className="seat-name">Lugar livre</span>
                </div>
                <div className="seat-body">
                  {isHost ? (
                    <button className="pill pill-green" onClick={() => send({ t: 'addBot' })}>
                      + Bot
                    </button>
                  ) : (
                    <span className="seat-wait">Esperando alguém…</span>
                  )}
                </div>
              </li>
            );
          }
          const mine = s.id === you;
          const ready = s.ready || s.id === room.hostId || s.bot;
          return (
            <li key={s.id} className={`seat-card ${mine ? 'is-me' : ''}`} style={{ '--seat': SEAT_COLORS[k] } as React.CSSProperties}>
              <div className="seat-head">
                <span className="seat-avatar">
                  <img src={pawnArt(s.pawn).idle} alt="" />
                  {ready && (
                    <svg className="seat-check" viewBox="0 0 40 40" aria-label="Pronto">
                      <path d="M6 21 L16 31 L35 9" fill="none" stroke="#fff" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 21 L16 31 L35 9" fill="none" stroke="#3fb950" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="seat-name">
                  {s.name}
                  {mine && <em> (você)</em>}
                </span>
              </div>
              <div className="seat-body">
                {mine && (
                  <button className="seat-arrow is-left" onClick={() => nextPawn(-1)} aria-label="Personagem anterior">
                    ‹
                  </button>
                )}
                <img className="seat-char" src={pawnArt(s.pawn).idle} alt={PAWNS[s.pawn]?.name ?? ''} />
                {mine && (
                  <button className="seat-arrow is-right" onClick={() => nextPawn(1)} aria-label="Próximo personagem">
                    ›
                  </button>
                )}
                <span className="seat-role">
                  {s.bot ? 'Bot' : s.id === room.hostId ? 'Criou a mesa' : !s.connected ? 'Desconectado' : s.ready ? 'Pronto' : 'Esperando'}
                </span>
                {isHost && !mine && (
                  <button className="seat-remove" onClick={() => send({ t: 'removeSeat', seat: s.id })} aria-label={`Tirar ${s.name}`}>
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M4 10 H16" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <footer className="table-foot">
        {isHost ? (
          <>
            <button className="pill pill-blue pill-play" disabled={!canStart} onClick={() => send({ t: 'start' })}>
              Jogar
            </button>
            <p className="foot-hint">
              {room.seats.length < 2
                ? 'Precisa de pelo menos 2 jogadores. Chame alguém ou coloque um bot.'
                : humansWaiting.length
                  ? `Esperando ${humansWaiting.map((s) => s.name).join(', ')} ficar pronto`
                  : `Tudo certo. Partida de até ${MODE_LABELS[room.settings.mode].minutes} minutos.`}
            </p>
          </>
        ) : (
          <>
            <button className={`pill pill-play ${me?.ready ? 'pill-gray' : 'pill-blue'}`} onClick={() => send({ t: 'ready', ready: !me?.ready })}>
              {me?.ready ? 'Não estou pronto' : 'Estou pronto'}
            </button>
            <p className="foot-hint">Quem criou a mesa aperta Jogar.</p>
          </>
        )}
      </footer>
      {rules && <RulesSheet onClose={() => setRules(false)} />}
    </main>
  );
}
